import SitePhoto from '@/components/Photo'
import SiteVideo from '@/components/Video'
import { localizeHref } from '@/i18n/href'
import { useI18n } from '@/i18n/I18nProvider'
import { Ctas, type SectionOf } from './shared'

export default function CtaSection({ section }: { section: SectionOf<'cta'> }) {
  const { locale } = useI18n()
  if (section.tone === 'card') {
    return (
      <div className="grid items-center gap-10 rounded-[40px] bg-band px-8 py-12 sm:px-14 sm:py-14 md:grid-cols-[minmax(0,1fr)_minmax(0,0.7fr)]">
        {/* The offer is first in the markup whichever side the picture is
            drawn on — `flip` moves the picture, not the reading order. */}
        <div className={section.flip ? 'md:order-last' : undefined}>
          <h2 className="max-w-xl text-2xl font-bold leading-snug text-primary sm:text-3xl">
            {section.heading}
          </h2>
          {section.sub && (
            <p className="mt-4 max-w-xl text-lg text-muted-foreground">{section.sub}</p>
          )}
          {section.body?.map((paragraph) => (
            <p key={paragraph} className="mt-4 max-w-xl text-lg leading-relaxed">
              {paragraph}
            </p>
          ))}
          {section.points?.length ? (
            <ul className="mt-6 max-w-xl space-y-3">
              {section.points.map((p) => (
                <li key={p} className="flex gap-3 text-lg leading-relaxed">
                  <span
                    aria-hidden
                    className="mt-[0.55rem] h-1.5 w-1.5 shrink-0 rounded-full bg-primary"
                  />
                  <span>{p}</span>
                </li>
              ))}
            </ul>
          ) : null}
          {section.outro?.map((paragraph) => (
            <p key={paragraph} className="mt-4 max-w-xl text-lg leading-relaxed">
              {paragraph}
            </p>
          ))}
          <Ctas items={section.ctas} />
        </div>
        {/* A film takes the picture's place and the picture becomes its poster,
            so a panel that has a still today gains a video by adding one line
            and shows the still again if the file ever goes missing. Same box,
            same cap, same corners — only the contents differ. */}
        {section.video ? (
          <SiteVideo
            src={section.video.src}
            title={section.video.title}
            poster={section.photo}
            className="mx-auto w-full max-w-md rounded-3xl"
          />
        ) : section.photo ? (
          section.photoHref ? (
            <a href={localizeHref(section.photoHref, locale)} className="mx-auto block w-full max-w-md">
              <SitePhoto
                photo={section.photo}
                sizes="(min-width: 768px) 40vw, 84vw"
                className="w-full rounded-3xl"
              />
            </a>
          ) : (
            <SitePhoto
              photo={section.photo}
              sizes="(min-width: 768px) 40vw, 84vw"
              /* `max-w-md` rather than `max-w-sm`: the column it sits in is
                 ~40% of the card, and a 384px cap left the artwork visibly
                 smaller than its own half of the panel. No page rendered a
                 card photograph before this one, so nothing else moves. */
              className="mx-auto w-full max-w-md rounded-3xl"
            />
          )
        ) : null}
      </div>
    )
  }
  {
    /* A band with a picture keeps the copy at its own measure on the left
       and puts the artwork in the empty right half — the grey band was
       built for text alone and reads as half-finished beside a chart the
       page never shows. Without a photo the wrapper collapses back to the
       single column it has always been. */
    const withPhoto = section.tone === 'band' && section.photo
    const copy = (
      <div className={section.tone === 'ink' ? '' : 'max-w-3xl'}>
        <h2 className="max-w-3xl text-3xl font-bold tracking-tight sm:text-4xl">
          {/* Clickable only when `headingHref` is set — no visible change,
              the anchor adds nothing but the cursor and a destination. */}
          {section.headingHref ? (
            <a href={localizeHref(section.headingHref, locale)}>{section.heading}</a>
          ) : (
            section.heading
          )}
        </h2>
        {section.sub && (
          <p
            className={`mt-6 max-w-2xl text-lg leading-relaxed ${
              section.tone === 'ink' ? 'opacity-80' : 'text-muted-foreground'
            }`}
          >
            {section.sub}
          </p>
        )}
        <Ctas items={section.ctas} onDark={section.tone === 'ink'} />
      </div>
    )
    if (!withPhoto || !section.photo) return copy
    const photo = (
      <SitePhoto
        photo={section.photo}
        sizes="(min-width: 768px) 48vw, 88vw"
        /* One step up from `max-w-lg`, and the column widened with it: the
           cap was binding before the column was, so raising only one of the
           two would have left the picture the same size. */
        className="mx-auto w-full max-w-xl rounded-3xl"
      />
    )
    return (
      <div className="grid items-center gap-10 md:grid-cols-[minmax(0,1fr)_minmax(0,0.95fr)]">
        {copy}
        {/* Clickable only when `photoHref` is set — no visible change, the
            anchor adds nothing but the cursor and a destination. */}
        {section.photoHref ? (
          <a href={localizeHref(section.photoHref, locale)} className="block">
            {photo}
          </a>
        ) : (
          photo
        )}
      </div>
    )
  }
}
